// App shell: composes the Header + Footer around the routed page, and wraps the
// page <Outlet/> in an AnimatePresence cross-fade + slide on route change
// (plan §5.3). The active language + <html lang> are synced by App.tsx's
// LocaleLayout (which owns the :lang param); this component is purely the shell.
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import Header from "./Header";
import Footer from "./Footer";
import { pageTransition } from "../motion/variants";

export default function Layout() {
  const location = useLocation();

  // Key the transition on the locale-stripped path so switching language does NOT
  // trigger a page-transition flash (it's the same page, just translated), while
  // navigating between pages does.
  const routeKey = location.pathname.replace(/^\/(zh|en)(?=\/|$)/, "");

  // Scroll to top when navigating to a different PAGE (not when toggling language,
  // which keeps routeKey). Without this the new page kept the previous scroll
  // position — on mobile you'd land mid-page, and since below-fold sections start
  // at opacity 0 (scroll-reveal), the new page looked blank until you scrolled.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [routeKey]);

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--bg)]">
      <Header />

      <main id="main-content" className="flex-1 focus:outline-none" tabIndex={-1}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={routeKey}
            variants={pageTransition}
            initial="initial"
            animate="enter"
            exit="exit"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      <Footer />
    </div>
  );
}
